/**
 * Links and notes in saved conversations (lib/chat-history.ts).
 *
 * A resumed chat must show the link card and the note the shopper sent, and
 * send them to the model again. Photos are still never saved (data URLs are
 * megabytes; a resumed photo turn keeps its words only, as before). A link is
 * saved with a slim copy of its preview so the card renders at once on
 * resume; an Amazon price in that copy is only ever shown while it is less
 * than a day old (the card checks).
 *
 * Pure, so it is unit-tested under the repo's node jest.
 */

import type { Attachment, Message } from 'ai';
import type { LinkPreview } from 'shared/src/types/link-preview.type';
import { isLinkPreview } from 'shared/src/types/link-preview.type';
import { linkToChatAttachment, noteToChatAttachment, readTextAttachments } from 'shared/src/utils/chat-attachments';
import { normalizeLinkUrl } from 'shared/src/utils/link-url';

export type ChatTurnAttachment =
  | { kind: 'link'; url: string; preview?: LinkPreview }
  | { kind: 'note'; text: string };

export const TRANSCRIPT_ATTACHMENTS_MAX = 6;
export const TRANSCRIPT_NOTE_MAX_CHARS = 3_000;

/** A preview small enough for localStorage: identity, photo, price, the way out. */
export function compactPreview(preview: LinkPreview): LinkPreview {
  if (preview.kind === 'amazon') return { ...preview, features: [], category: null };
  if (preview.kind === 'product' || preview.kind === 'page') {
    const slim = { ...preview, description: null, bullets: [] };
    delete slim.excerpt;
    return slim;
  }
  return preview;
}

/** The links and notes of a user message, ready to save. Undefined when there are none. */
export function transcriptAttachmentsFromMessage(
  msg: Pick<Message, 'experimental_attachments'>,
  previewFor: (url: string) => LinkPreview | null | undefined,
): ChatTurnAttachment[] | undefined {
  const { links, notes } = readTextAttachments(msg.experimental_attachments);
  const out: ChatTurnAttachment[] = [];
  for (const raw of links) {
    const normalized = normalizeLinkUrl(raw);
    if (!normalized || out.some((a) => a.kind === 'link' && a.url === normalized.url)) continue;
    const preview = previewFor(normalized.url);
    out.push({ kind: 'link', url: normalized.url, ...(preview ? { preview: compactPreview(preview) } : {}) });
  }
  for (const text of notes) out.push({ kind: 'note', text: text.slice(0, TRANSCRIPT_NOTE_MAX_CHARS) });
  return out.length > 0 ? out.slice(0, TRANSCRIPT_ATTACHMENTS_MAX) : undefined;
}

/** Saved attachments back into chat attachments (same data URLs the bar sends). */
export function chatAttachmentsFromTranscript(items: readonly ChatTurnAttachment[] | undefined): Attachment[] {
  if (!Array.isArray(items)) return [];
  const out: Attachment[] = [];
  for (const item of items) {
    if (item?.kind === 'link' && typeof item.url === 'string') out.push(linkToChatAttachment(item.url));
    else if (item?.kind === 'note' && typeof item.text === 'string') out.push(noteToChatAttachment(item.text));
  }
  return out;
}

/** The link previews a saved conversation carries, to seed the card store on resume. */
export function previewsFromTranscriptAttachments(
  lists: ReadonlyArray<readonly ChatTurnAttachment[] | undefined>,
): Array<[string, LinkPreview]> {
  const out: Array<[string, LinkPreview]> = [];
  for (const items of lists) {
    for (const item of items ?? []) {
      if (item?.kind === 'link' && isLinkPreview(item.preview)) out.push([item.url, item.preview]);
    }
  }
  return out;
}
